/**
 * The error reporting tools use Honeybadger, via our custom Honeybadger reporter at
 * `app/services/honeybadger`, because Honeybadger's JavaScript client library currently sends
 * reports via GET, making requests fail for large payloads.
 */

import { Set, fromJS } from 'immutable';
import appConfig from 'app/config/client';
import Logger from 'app/services/logger';
import realSendErrorReport, {
  reset as resetSendErrorReportState,
} from 'app/services/honeybadger/sendErrorReport';
import {
  getLocalClientVersion,
  getLocalClientRelease,
  getLocalBreakingChangeTimestamp,
} from 'app/utils/clientVersion';

// Keep our own reference of context to merge in when sending up to honeybadger.
const defaultHoneybadgerDisabled = appConfig.honeybadger.disabled || false;

// Removes sensitive data from logs and state
export const SYMBOL_REPLACEMENT = '[SYMBOL]';
export const SENSITIVE_FIELD_REPLACEMENT = '[FILTERED]';

/**
 * TODO: Switch to pattern matching.
 */
const SENSITIVE_FIELDS = new Set([
  'password',
  'passwordConfirmation',
  'currentPassword',
  'password_confirmation',
  'current_password',
  'access_token',
  'refresh_token',
  'AUTH',
  'contents', // chat message contents
]);

export const NOTIFY_ERROR = 'notify/error';
export const NOTIFY_CONTEXT = 'notify/context';

let beforeSendCallback;

/**
 * Notify takes an error object and then a custom message object. The most
 * important parameters to pass are name and context (which provides more) information
 * on the error. Check the API reference for other fields.
 *
 * @param {Error} err
 * @param {Object} options
 * @param {String} options.name
 * @param {Object} options.context        meta data to pass along
 */
export const notify = (err, originalOptions = {}, ...args) => {
  const {
    // Dependency injection for tests
    honeybadgerDisabled = defaultHoneybadgerDisabled,
    sendErrorReport = realSendErrorReport,
    ...options
  } = originalOptions;

  try {
    if (honeybadgerDisabled) {
      return;
    }

    // Keys in the error report will be ordered in the same order they are added to the context
    // here.
    const context = {};

    try {
      // If the error object is `stringify`able, add it to the context.
      // However, normal `Error` objects just return "{}" when stringified, which isn't very useful,
      // so don't bother adding that to the context.
      if (JSON.stringify(err) !== '{}') {
        context[NOTIFY_ERROR] = err;
      }
    } catch (stringifyError) {
      context[NOTIFY_ERROR] = `Could not stringify error: ${stringifyError}`;
    }

    if (options.context) {
      context[NOTIFY_CONTEXT] = options.context;
    }

    context.clientVersion = getLocalClientVersion();
    context.clientRelease = getLocalClientRelease();
    context.breakingChangeTimestamp = getLocalBreakingChangeTimestamp();

    context.logs = Logger.getLogs();

    if (beforeSendCallback) {
      // Allow callback to mutate the object before it is sanitized and sent to Honeybadger.
      beforeSendCallback(err, context);
    }

    // Make sure that all ImmutableJS objects nested inside the error and context get converted to
    // plain JS objects.
    //
    // Note: fromJS will throw an error if the object has circular references.
    // TODO: Maybe include object up to a fixed maximum depth if it has circular references.
    const plainObjectContext = fromJS(context).toJS();

    const extendedOptions = {
      ...options,
      context: sanitizeContext(plainObjectContext),
    };

    sendErrorReport(err, extendedOptions);
  } catch (reason) {
    // Call notify on honeybadger if sanitizing the context fails.
    sendErrorReport(
      reason,
      {
        name: 'ErrorReporter.Notify',
        context: {
          arguments: {
            error: err,
            options,
            args,
          },
        },
      },
    );
  }
};

/**
 * Helper for sanitizing context. Recursively goes through context, clearing
 * sensitive data.
 *
 * @param {Object|Array} ctx
 * @returns {Object|Array}
 */
const sanitizeContext = (ctx) => {
  const iterate = (obj, f) => {
    if (Array.isArray(obj)) {
      return obj.map(f);
    } else {
      const data = {};
      Object.keys(obj).forEach(key => (data[key] = f(obj[key], key)));
      return data;
    }
  };

  return iterate(ctx, (value, key) => {
    if (SENSITIVE_FIELDS.has(key)) {
      return SENSITIVE_FIELD_REPLACEMENT;
    } else if (typeof value === 'symbol' || value instanceof Symbol) {
      return SYMBOL_REPLACEMENT;
    } else if (value instanceof Error) {
      // JSON.stringify on error objects does not include their name/message, so extract them and
      // convert it to a plain object.
      return { name: value.name, message: value.message, ...sanitizeContext(value) };
    } else if (typeof value === 'object' && value !== null) {
      return sanitizeContext(value);

      // So this is consistent between polyfill and supporting browsers, check both the typeof
      // and the instanceof. A polyfilled Symbol should technically be converted to
      // "[Object object]", but this would cause tests to fail.
    } else {
      return value;
    }
  });
};

export const reset = () => {
  beforeSendCallback = undefined;
  resetSendErrorReportState();
};

export const setBeforeSendCallback = (func) => {
  if (beforeSendCallback !== undefined) {
    throw new Error('Cannot set beforeSendCallback more than once');
  }

  beforeSendCallback = func;
};
